import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const Home = () => {
    const { user } = useAuth();
    const { t } = useTranslation();

    return (
        <div className="page-transition">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">
                            {t('home.hero.title')}
                        </h1>
                        <p className="text-xl md:text-2xl mb-8">
                            {t('home.hero.subtitle')}
                        </p>
                        <p className="text-lg mb-8 text-blue-100">
                            {t('home.hero.description')}
                        </p>
                        {!user ? (
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link
                                    to="/register"
                                    className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors text-lg"
                                >
                                    {t('home.hero.getStarted')}
                                </Link>
                                <Link
                                    to="/login"
                                    className="bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-800 active:bg-blue-900 transition-colors text-lg border-2 border-white min-h-touch"
                                >
                                    {t('home.hero.signIn')}
                                </Link>
                            </div>
                        ) : (
                            <Link
                                to="/dashboard"
                                className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors text-lg"
                            >
                                {t('home.hero.goToDashboard')}
                            </Link>
                        )}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">{t('home.features.title')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="card text-center">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold mb-3">{t('home.features.neutral.title')}</h3>
                            <p className="text-gray-600">
                                {t('home.features.neutral.description')}
                            </p>
                        </div>

                        <div className="card text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold mb-3">{t('home.features.voice.title')}</h3>
                            <p className="text-gray-600">
                                {t('home.features.voice.description')}
                            </p>
                        </div>

                        <div className="card text-center">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold mb-3">{t('home.features.smart.title')}</h3>
                            <p className="text-gray-600">
                                {t('home.features.smart.description')}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Subscription Plans */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">{t('home.subscription.title')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {/* Free Trial */}
                        <div className="card border-2 border-green-500 h-full flex flex-col">
                            <div className="text-center mb-6">
                                <span className="bg-green-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                                    {t('home.subscription.freeTrial.badge')}
                                </span>
                            </div>
                            <h3 className="text-2xl font-bold text-center mb-4">{t('home.subscription.freeTrial.title')}</h3>
                            <p className="text-3xl font-bold text-center mb-6">{t('home.subscription.freeTrial.price')}</p>
                            <ul className="space-y-3 mb-6">
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>{t('home.subscription.freeTrial.features.offTheShelf')}</span>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>{t('home.subscription.freeTrial.features.madeToMeasure')}</span>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>{t('home.subscription.freeTrial.features.voiceEnabled')}</span>
                                </li>
                            </ul>
                            <Link
                                to="/register?plan=free"
                                className="block w-full bg-green-500 text-white text-center px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors mt-auto"
                            >
                                {t('home.subscription.freeTrial.cta')}
                            </Link>
                        </div>

                        {/* Basic */}
                        <div className="card h-full flex flex-col">
                            <div className="text-center mb-6">
                                <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                                    {t('home.subscription.basic.badge')}
                                </span>
                            </div>
                            <h3 className="text-2xl font-bold text-center mb-4">{t('home.subscription.basic.title')}</h3>
                            <p className="text-3xl font-bold text-center mb-6">
                                {t('home.subscription.basic.price')}<span className="text-lg text-gray-600">{t('home.subscription.basic.duration')}</span>
                            </p>
                            <ul className="space-y-3 mb-6">
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>{t('home.subscription.basic.features.offTheShelf')}</span>
                                </li>
                                <li className="flex items-start text-gray-400">
                                    <svg className="w-5 h-5 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    <span>{t('home.subscription.basic.features.madeToMeasure')}</span>
                                </li>
                            </ul>
                            <Link
                                to="/register?plan=basic"
                                className="block w-full bg-blue-500 text-white text-center px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors mt-auto"
                            >
                                {t('home.subscription.basic.cta')}
                            </Link>
                        </div>

                        {/* Pro */}
                        <div className="card border-2 border-purple-500 h-full flex flex-col">
                            <div className="text-center mb-6">
                                <span className="bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                                    {t('home.subscription.pro.badge')}
                                </span>
                            </div>
                            <h3 className="text-2xl font-bold text-center mb-4">{t('home.subscription.pro.title')}</h3>
                            <p className="text-3xl font-bold text-center mb-6">
                                {t('home.subscription.pro.price')}<span className="text-lg text-gray-600">{t('home.subscription.pro.duration')}</span>
                            </p>
                            <ul className="space-y-3 mb-6">
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>{t('home.subscription.pro.features.offTheShelf')}</span>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>{t('home.subscription.pro.features.madeToMeasure')}</span>
                                </li>
                                <li className="flex items-start">
                                    <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>{t('home.subscription.pro.features.prioritySupport')}</span>
                                </li>
                            </ul>
                            <Link
                                to="/register?plan=pro"
                                className="block w-full bg-purple-500 text-white text-center px-6 py-3 rounded-lg font-semibold hover:bg-purple-600 transition-colors mt-auto"
                            >
                                {t('home.subscription.pro.cta')}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Partners Section */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-4">{t('home.partners.title')}</h2>
                    <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
                        {t('home.partners.description')}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
                        {['MediCompression', 'VeinCare Pro', 'LegHealth Solutions', 'ComfortMed'].map((partner) => (
                            <div key={partner} className="card text-center">
                                <div className="w-20 h-20 bg-gray-200 rounded-full mx-auto mb-3 flex items-center justify-center">
                                    <span className="text-2xl font-bold text-gray-400">{partner.charAt(0)}</span>
                                </div>
                                <p className="font-semibold text-sm">{partner}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-blue-600 text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold mb-6">
                        {t('home.cta.title')}
                    </h2>
                    <p className="text-xl mb-8 max-w-2xl mx-auto">
                        {t('home.cta.description')}
                    </p>
                    {!user && (
                        <Link
                            to="/register"
                            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors text-lg"
                        >
                            {t('home.cta.button')}
                        </Link>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Home;
